import { useState, useEffect } from 'react';

// French is the default (the customer base is French-speaking); English is
// one click away. The choice is remembered, and ?lang=en / ?lang=fr works too.
const KEY = 'fat_lang';
const EVENT = 'fat-lang-change';

function readLang() {
  try {
    const q = new URLSearchParams(window.location.search).get('lang');
    if (q === 'en' || q === 'fr') {
      localStorage.setItem(KEY, q);
      return q;
    }
    const v = localStorage.getItem(KEY);
    if (v === 'en' || v === 'fr') return v;
  } catch {
    // storage blocked: fall through to the default
  }
  return 'fr';
}

let current = readLang();

export function currentLang() {
  return current;
}

export function setLang(lang) {
  current = lang;
  try {
    localStorage.setItem(KEY, lang);
  } catch {
    // ignore
  }
  document.documentElement.lang = lang;
  window.dispatchEvent(new Event(EVENT));
}

export function useLang() {
  const [lang, setState] = useState(current);
  useEffect(() => {
    document.documentElement.lang = current;
    const handler = () => setState(current);
    window.addEventListener(EVENT, handler);
    return () => window.removeEventListener(EVENT, handler);
  }, []);
  return lang;
}

export function LangSwitch() {
  const lang = useLang();
  const btn = (code) => (
    <button
      type="button"
      className={'lang-btn' + (lang === code ? ' lang-btn--active' : '')}
      onClick={() => setLang(code)}
      aria-pressed={lang === code}
    >
      {code.toUpperCase()}
    </button>
  );
  return (
    <div className="lang-switch" role="group" aria-label="Language / Langue">
      {btn('fr')}
      <span>|</span>
      {btn('en')}
    </div>
  );
}

const D = {
  fr: {
    navFeatures: 'Fonctionnalités',
    navSecurity: 'Sécurité',
    navContact: 'Contact',
    navFind: 'Retrouver ma réservation',
    navDownload: 'Télécharger l’app',
    footerCopy: '© 2025 Afritrip. Tous droits réservés.',
    footerTerms: 'Conditions',
    footerPrivacy: 'Confidentialité',
    footerContact: 'Contact',
    // booking page
    findTitle: 'Retrouvez votre réservation',
    findSub: 'Saisissez votre référence de réservation et votre nom pour voir les détails de votre voyage.',
    refLabel: 'Référence de réservation',
    refPlaceholder: 'ex. 123-456-789',
    lastLabel: 'Nom de famille',
    lastPlaceholder: 'ex. Doe',
    searching: 'Recherche…',
    findBtn: 'Retrouver ma réservation',
    docTitleFind: 'Retrouver ma réservation — FlyAfritrip',
    docTitleBooking: (ref) => `Réservation ${ref} — FlyAfritrip`,
    errNotFound: 'Nous n’avons trouvé aucune réservation avec cette référence et ce nom. Vérifiez les deux et réessayez.',
    errGeneric: 'Une erreur est survenue. Veuillez réessayer.',
    apiUnreachable: 'Impossible de joindre le serveur. Vérifiez votre connexion et réessayez.',
    apiNotFound: 'Aucune réservation trouvée avec cette référence et ce nom.',
    apiRate: 'Trop de tentatives. Veuillez patienter une minute puis réessayer.',
    apiFailed: 'Une erreur est survenue lors de la recherche de votre réservation. Veuillez réessayer.',
    bookingRef: 'Référence de réservation',
    labelCancelled: 'STATUT DE LA RÉSERVATION',
    labelIssued: 'E-BILLET',
    labelConfirmed: 'CONFIRMATION DE RÉSERVATION',
    badgeCancelled: 'RÉSERVATION ANNULÉE',
    badgeIssued: 'BILLET ÉMIS',
    badgeConfirmed: 'RÉSERVATION CONFIRMÉE',
    cancelledNote: 'Cette réservation a été annulée. Pour toute question, contactez support@flyafritrip.com.',
    cabinLine: 'Classe :',
    stubRef: 'RÉF. RÉSERVATION',
    stubPnr: 'PNR COMPAGNIE',
    stubClass: 'CLASSE',
    stubSeat: 'SIÈGE',
    scan: 'SCANNEZ ICI',
    passengers: 'Passagers',
    colName: 'Nom',
    colEticket: 'E-billet',
    totalPaid: 'Total payé',
    seatsFee: 'dont sièges',
    dlCancelled: 'Télécharger le récapitulatif (PDF)',
    dlIssued: 'Télécharger l’e-billet (PDF)',
    dlConfirmed: 'Télécharger la confirmation (PDF)',
    preparing: 'Préparation du PDF…',
    // pdf
    pdfLabelCancelled: 'STATUT DE LA RÉSERVATION',
    pdfLabelIssued: 'E-BILLET',
    pdfLabelConfirmed: 'CONFIRMATION DE RÉSERVATION',
    pdfTripTo: 'Voyage vers',
    pdfRef: 'Référence de réservation',
    pdfPhone: 'Téléphone',
    pdfSentTo: 'Envoyé à',
    pdfColName: 'NOM',
    pdfColTicket: 'NUMÉRO DE BILLET',
    pdfQuestions: 'Une question sur cette réservation ?  support@flyafritrip.com',
    pdfArrive: 'Merci d’arriver à l’aéroport au moins 2 heures avant le départ.',
    // baggage + fare conditions
    bagTitle: 'Franchise bagages',
    bagFareTitle: 'Conditions du tarif',
    bagPersonal: 'Article personnel',
    bagCabin: 'Bagage cabine',
    bagChecked: 'Bagage en soute',
    bagChecked2: '2e bagage en soute',
    bagLeg: 'Trajet',
    bagInc: 'Inclus',
    bagCha: 'Avec supplément',
    bagNof: 'Non proposé',
    condInc: 'Gratuit',
    condCha: 'Avec frais',
    condNof: 'Non autorisé',
    bagPieces: (n) => (n === 1 ? '1 pièce' : `${n} pièces`),
    bagKg: (n) => `${n} kg`,
    bagLb: (n) => `${n} lb`,
    bagUnavailable: 'La franchise bagages de ce billet n’est pas encore disponible. Merci de la vérifier auprès de la compagnie avant le voyage.',
    bagNote: 'Les conditions exactes (poids, dimensions) sont celles de la compagnie aérienne.',
    cond: { refund_before_departure: 'Remboursement avant le départ', refund_after_departure: 'Remboursement après le départ', exchange_before_departure: 'Modification avant le départ', exchange_after_departure: 'Modification après le départ', void_before_departure: 'Annulation le jour de l’achat', void_after_departure: 'Annulation après le départ' },
    // formats
    depart: 'Départ',
    ret: 'Retour',
    flightN: (n) => `Vol ${n}`,
    direct: 'Direct',
    oneStop: '1 escale',
    stops: (n) => `${n} escales`,
    cabins: { economy: 'Économique', premium_economy: 'Premium économique', premiumeconomy: 'Premium économique', business: 'Affaires', first: 'Première' }
  },
  en: {
    navFeatures: 'Features',
    navSecurity: 'Security',
    navContact: 'Contact',
    navFind: 'Find my booking',
    navDownload: 'Download the app',
    footerCopy: '© 2025 Afritrip. All rights reserved.',
    footerTerms: 'Terms',
    footerPrivacy: 'Privacy',
    footerContact: 'Contact',
    findTitle: 'Find your booking',
    findSub: 'Enter your booking reference and last name to view your trip details.',
    refLabel: 'Booking reference',
    refPlaceholder: 'e.g. 123-456-789',
    lastLabel: 'Last name',
    lastPlaceholder: 'e.g. Doe',
    searching: 'Searching…',
    findBtn: 'Find my booking',
    docTitleFind: 'Find my booking — FlyAfritrip',
    docTitleBooking: (ref) => `Booking ${ref} — FlyAfritrip`,
    errNotFound: 'We couldn’t find a booking with that reference and last name. Double-check both and try again.',
    errGeneric: 'Something went wrong. Please try again.',
    apiUnreachable: 'Could not reach the server. Check your connection and try again.',
    apiNotFound: 'No booking found with that reference and last name.',
    apiRate: 'Too many attempts. Please wait a minute and try again.',
    apiFailed: 'Something went wrong looking up your booking. Please try again.',
    bookingRef: 'Booking reference',
    labelCancelled: 'BOOKING STATUS',
    labelIssued: 'E-TICKET',
    labelConfirmed: 'BOOKING CONFIRMATION',
    badgeCancelled: 'BOOKING CANCELLED',
    badgeIssued: 'TICKET ISSUED',
    badgeConfirmed: 'BOOKING CONFIRMED',
    cancelledNote: 'This booking has been cancelled. If you have any questions, please contact support@flyafritrip.com.',
    cabinLine: 'Cabin:',
    stubRef: 'BOOKING REF',
    stubPnr: 'AIRLINE PNR',
    stubClass: 'CLASS',
    stubSeat: 'SEAT',
    scan: 'SCAN HERE',
    passengers: 'Passengers',
    colName: 'Name',
    colEticket: 'E-ticket',
    totalPaid: 'Total paid',
    seatsFee: 'including seats',
    dlCancelled: 'Download booking summary (PDF)',
    dlIssued: 'Download e-ticket (PDF)',
    dlConfirmed: 'Download booking confirmation (PDF)',
    preparing: 'Preparing PDF…',
    pdfLabelCancelled: 'BOOKING STATUS',
    pdfLabelIssued: 'E-TICKET',
    pdfLabelConfirmed: 'BOOKING CONFIRMATION',
    pdfTripTo: 'Trip to',
    pdfRef: 'Booking reference',
    pdfPhone: 'Contact phone',
    pdfSentTo: 'Sent to',
    pdfColName: 'NAME',
    pdfColTicket: 'E-TICKET NUMBER',
    pdfQuestions: 'Questions about this booking?  support@flyafritrip.com',
    pdfArrive: 'Please arrive at the airport at least 2 hours before departure.',
    // baggage + fare conditions
    bagTitle: 'Baggage allowance',
    bagFareTitle: 'Fare conditions',
    bagPersonal: 'Personal item',
    bagCabin: 'Cabin bag',
    bagChecked: 'Checked bag',
    bagChecked2: '2nd checked bag',
    bagLeg: 'Flight',
    bagInc: 'Included',
    bagCha: 'For a fee',
    bagNof: 'Not offered',
    condInc: 'Free of charge',
    condCha: 'Fee applies',
    condNof: 'Not permitted',
    bagPieces: (n) => (n === 1 ? '1 piece' : `${n} pieces`),
    bagKg: (n) => `${n} kg`,
    bagLb: (n) => `${n} lb`,
    bagUnavailable: 'The baggage allowance for this ticket is not available yet. Please check it with the airline before you travel.',
    bagNote: 'Exact weight and size limits are set by the airline.',
    cond: { refund_before_departure: 'Refund before departure', refund_after_departure: 'Refund after departure', exchange_before_departure: 'Change before departure', exchange_after_departure: 'Change after departure', void_before_departure: 'Cancellation on the day of purchase', void_after_departure: 'Cancellation after departure' },
    depart: 'Departure',
    ret: 'Return',
    flightN: (n) => `Flight ${n}`,
    direct: 'Direct',
    oneStop: '1 stop',
    stops: (n) => `${n} stops`,
    cabins: { economy: 'Economy', premium_economy: 'Premium economy', premiumeconomy: 'Premium economy', business: 'Business', first: 'First' }
  }
};

export function tr(key, ...args) {
  const v = D[current][key] ?? D.fr[key];
  return typeof v === 'function' ? v(...args) : v;
}

// ---- landing page copy ------------------------------------------------------

export const LANDING = {
  fr: {
    docTitle: 'FlyAfritrip — Votre voyage commence ici',
    heroA: 'Votre voyage commence',
    heroB: 'avec',
    heroC: 'dans MaxIt',
    heroSub: "Comparez les tarifs, gérez votre voyage, payez avec Orange Money et personnalisez votre expérience via l'application FlyAfriTrip, disponible sur MaxIt.",
    phoneAlt: 'App sur téléphone',
    maxitSmall: 'Télécharger sur',
    badges: ['Sécurité Orange Money', 'Paiement mobile simplifié', 'Support rapide', 'Gestion du voyage'],
    features: [
      { badge: 'PAIEMENT MOBILE', title: 'Payez facilement avec Orange Money', body: 'Réservez vos vols rapidement grâce à un paiement mobile simple, sécurisé et adapté aux voyageurs africains.', checks: ['Paiement rapide et sécurisé', 'Compatible mobile money', 'Confirmation instantanée'], imageAlt: 'Paiement Orange Money' },
      { badge: 'MEILLEURS TARIFS', title: 'Trouvez les combinaisons de vols les plus avantageuses', body: 'FlyAfritrip compare plusieurs compagnies aériennes pour vous proposer les itinéraires les plus économiques et les plus pratiques.', checks: ['le meilleur prix,', 'le trajet le plus rapide,', 'ou les vols directs.'], imageAlt: 'Résultats de recherche' },
      { badge: 'GESTION DU VOYAGE', title: "Gérez votre réservation dans l'app", body: "Modifiez votre voyage, consultez vos billets, téléchargez vos documents et accédez à tous les détails de votre réservation en quelques secondes. Plus besoin d'appeler ou d'envoyer des emails : tout est accessible depuis votre espace personnel.", checks: ['Billets électroniques disponibles instantanément', 'Gestion des passagers', 'Téléchargement des documents'], imageAlt: 'Gestion du voyage' },
      { badge: 'OPTIONS PERSONNALISÉES', title: 'Personnalisez votre expérience de voyage', body: 'Ajoutez les options qui correspondent à vos besoins : choix des sièges, bagages supplémentaires, assurance voyage et options de flexibilité. Vous gardez le contrôle total de votre voyage avant même le décollage.', checks: ['Sélection des sièges', 'Bagages supplémentaires', 'Assurance voyage'], imageAlt: 'Options personnalisées' },
      { badge: 'SUIVI DU VOYAGE', title: 'Consultez tous les détails de votre itinéraire', body: 'Suivez chaque étape de votre voyage grâce à une vue détaillée de vos vols, escales et horaires. Recevez toutes les informations importantes au même endroit pour voyager sereinement.', checks: ['Horaires détaillés', 'Informations sur les escales', 'Détails des compagnies aériennes'], imageAlt: 'Suivi du voyage' },
      { badge: 'ASSISTANCE 24/7', title: 'Une assistance disponible à tout moment', body: "Besoin d'aide pour modifier votre réservation ou obtenir des informations sur votre voyage ? Contactez notre équipe 24h/24 et 7j/7 via le chatbot, WhatsApp ou par téléphone pour une assistance rapide et efficace.", checks: ['Chatbot', 'Numéro WhatsApp', 'Numéro de téléphone', 'Assistance 24/7'], imageAlt: 'Assistance 24/7' }
    ],
    testimonialsBadge: 'ILS NOUS FONT CONFIANCE',
    testimonialsTitleA: 'Des voyageurs satisfaits à',
    testimonialsTitleB: "travers l'Afrique",
    prev: 'Précédent',
    next: 'Suivant',
    testimonials: [
      '"Le paiement avec Orange Money a été super rapide. J’ai réservé mon vol en quelques minutes seulement."',
      '"J’ai trouvé un vol beaucoup moins cher grâce aux combinaisons proposées par l’application."',
      '"J’ai adoré pouvoir choisir mes sièges et ajouter mes bagages directement dans l’application."',
      '"Grâce à FlyAfritrip, j’ai pu comparer plusieurs vols et choisir le meilleur tarif en quelques clics. Vraiment pratique !"',
      '"La gestion de ma réservation depuis l’application est très simple. J’ai pu modifier mon vol sans aucun problème."',
      '"Le service client est disponible 24h/24. J’ai eu une réponse immédiate via WhatsApp pour ma demande d’assistance."'
    ],
    locations: ["Abidjan, Côte d'Ivoire", 'Bamako, Mali', 'Dakar, Sénégal', 'Accra, Ghana', 'Dakar, Sénégal', 'Conakry, Guinée'],
    contactBadge: 'CONTACT',
    contactTitle: 'Contactez-Nous',
    countryLabel: 'Pays',
    phoneLabel: 'Numéro de téléphone',
    countries: { sn: 'Sénégal', ml: 'Mali', ci: "Côte d'Ivoire" },
    faqBadge: 'FAQS',
    faqTitle: 'Tout ce que vous devez savoir',
    faqs: [
      { q: 'Comment payer mon vol sur FlyAfritrip ?', a: "Vous pouvez payer votre vol facilement via Orange Money, directement dans l'application MaxIt. Le paiement est rapide, sécurisé et adapté aux voyageurs africains." },
      { q: "Puis-je gérer ma réservation après l'achat ?", a: "Oui ! Vous pouvez modifier votre voyage, consulter vos billets, télécharger vos documents et accéder à tous les détails de votre réservation depuis votre espace personnel dans l'application." },
      { q: 'Puis-je ajouter des bagages ou choisir mon siège ?', a: "Absolument. FlyAfritrip vous permet d'ajouter des bagages supplémentaires, de choisir vos sièges et d'ajouter une assurance voyage directement lors de la réservation ou après l'achat." }
    ],
    ctaEyebrow: "Télécharger l'application",
    ctaTitle: 'Votre prochain voyage commence ici',
    ctaSub: "Réservez vos vols, gérez votre réservation et voyagez en toute simplicité avec FlyAfritrip dans l'application de Orange Maxit"
  },
  en: {
    docTitle: 'FlyAfritrip — Your journey starts here',
    heroA: 'Your journey starts',
    heroB: 'with',
    heroC: 'in MaxIt',
    heroSub: 'Compare fares, manage your trip, pay with Orange Money and personalise your experience through the FlyAfriTrip app, available on MaxIt.',
    phoneAlt: 'App on a phone',
    maxitSmall: 'Download on the',
    badges: ['Orange Money security', 'Simplified mobile payment', 'Fast support', 'Trip management'],
    features: [
      { badge: 'MOBILE PAYMENT', title: 'Pay easily with Orange Money', body: 'Book your flights quickly with a simple, secure mobile payment designed for African travellers.', checks: ['Fast and secure payment', 'Mobile money compatible', 'Instant confirmation'], imageAlt: 'Orange Money payment' },
      { badge: 'BEST FARES', title: 'Find the best flight combinations', body: 'FlyAfritrip compares several airlines to offer you the cheapest and most convenient itineraries.', checks: ['the best price,', 'the fastest route,', 'or direct flights.'], imageAlt: 'Search results' },
      { badge: 'TRIP MANAGEMENT', title: 'Manage your booking in the app', body: 'Change your trip, view your tickets, download your documents and access every detail of your booking in seconds. No more calling or emailing: everything is available from your personal space.', checks: ['E-tickets available instantly', 'Passenger management', 'Document downloads'], imageAlt: 'Trip management' },
      { badge: 'CUSTOM OPTIONS', title: 'Personalise your travel experience', body: 'Add the options that suit your needs: seat selection, extra baggage, travel insurance and flexibility options. You stay in full control of your trip before you even take off.', checks: ['Seat selection', 'Extra baggage', 'Travel insurance'], imageAlt: 'Custom options' },
      { badge: 'TRIP TRACKING', title: 'See every detail of your itinerary', body: 'Follow each step of your journey with a detailed view of your flights, stopovers and times. Get all the important information in one place and travel with peace of mind.', checks: ['Detailed schedules', 'Stopover information', 'Airline details'], imageAlt: 'Trip tracking' },
      { badge: '24/7 SUPPORT', title: 'Support available at any time', body: 'Need help changing your booking or getting information about your trip? Contact our team 24 hours a day, 7 days a week through the chatbot, WhatsApp or by phone for fast, effective help.', checks: ['Chatbot', 'WhatsApp number', 'Phone number', '24/7 support'], imageAlt: '24/7 support' }
    ],
    testimonialsBadge: 'THEY TRUST US',
    testimonialsTitleA: 'Happy travellers',
    testimonialsTitleB: 'across Africa',
    prev: 'Previous',
    next: 'Next',
    testimonials: [
      '"Paying with Orange Money was super fast. I booked my flight in just a few minutes."',
      '"I found a much cheaper flight thanks to the combinations offered by the app."',
      '"I loved being able to choose my seats and add my baggage directly in the app."',
      '"Thanks to FlyAfritrip I could compare several flights and pick the best fare in a few clicks. Really convenient!"',
      '"Managing my booking from the app is very simple. I was able to change my flight without any problem."',
      '"Customer service is available 24/7. I got an immediate reply on WhatsApp for my support request."'
    ],
    locations: ['Abidjan, Ivory Coast', 'Bamako, Mali', 'Dakar, Senegal', 'Accra, Ghana', 'Dakar, Senegal', 'Conakry, Guinea'],
    contactBadge: 'CONTACT',
    contactTitle: 'Contact us',
    countryLabel: 'Country',
    phoneLabel: 'Phone number',
    countries: { sn: 'Senegal', ml: 'Mali', ci: 'Ivory Coast' },
    faqBadge: 'FAQS',
    faqTitle: 'Everything you need to know',
    faqs: [
      { q: 'How do I pay for my flight on FlyAfritrip?', a: 'You can easily pay for your flight with Orange Money, directly in the MaxIt app. Payment is fast, secure and designed for African travellers.' },
      { q: 'Can I manage my booking after purchase?', a: 'Yes! You can change your trip, view your tickets, download your documents and access every detail of your booking from your personal space in the app.' },
      { q: 'Can I add baggage or choose my seat?', a: 'Absolutely. FlyAfritrip lets you add extra baggage, choose your seats and add travel insurance directly when booking or after purchase.' }
    ],
    ctaEyebrow: 'Download the app',
    ctaTitle: 'Your next trip starts here',
    ctaSub: 'Book your flights, manage your booking and travel with ease with FlyAfritrip in the Orange Maxit app'
  }
};
